import { Link } from "react-router-dom";
import { Search, Handshake, Truck, ShieldCheck, Sparkles, Calculator } from "lucide-react";

const STEPS = [
  { n: "01", t: "Discovery", d: "Share your needs through the AI Concierge, Find My Car form, or a quick 20-minute call. We capture budget, lease vs finance, trade-in, timeline, and must-haves.", icon: Sparkles },
  { n: "02", t: "Sourcing", d: "Our broker team taps into our nationwide dealer + wholesale network. We surface 3-5 vehicles that meet your brief, with side-by-side payment + total-cost comparison.", icon: Search },
  { n: "03", t: "Negotiation", d: "We negotiate price, fees, and lease terms on your behalf. No add-ons you didn't ask for. No four-square games. You see the final numbers before signing.", icon: Handshake },
  { n: "04", t: "Financing & Trade", d: "Pre-qualify through our lender network. Send your trade VIN; we get a wholesale-backed range. We handle payoff, registration, and any tax credit pass-through.", icon: Calculator },
  { n: "05", t: "Delivery", d: "Sign digitally. We arrange enclosed or open transport, or you pick up. White-glove handoff at your home or office — most clients close in 5-10 days.", icon: Truck },
  { n: "06", t: "Aftercare", d: "Your broker stays available for service questions, lease-end planning, and warranty/service contract decisions. Concierge access for as long as you own the vehicle.", icon: ShieldCheck },
];

const FAQ = [
  { q: "Are you a dealership?", a: "No. 555 Auto Brokers is a licensed auto broker — we represent you, not the dealer. Our incentives are aligned with yours." },
  { q: "How do you make money?", a: "Broker fees (transparent, flat), dealer partnerships, lender referrals, and optional add-on referrals (transport, warranty). All disclosed up front." },
  { q: "Can I really get a car without going to a dealership?", a: "Yes. 90%+ of our closes are 100% remote with home delivery. You sign digitally; we ship the vehicle." },
  { q: "Do you only do luxury cars?", a: "We source any brand. Most clients gravitate toward luxury or premium, but Honda Accords and F-150s are common too." },
  { q: "How much do you save me?", a: "Average client savings vs. walking into a dealer alone is $2,400 - $5,800 on luxury leases. Some deals save more — some less. We tell you up front." },
];

export default function HowItWorks() {
  return (
    <div data-testid="how-it-works-page">
      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16 md:py-24">
        <p className="label-mono mb-3">How It Works</p>
        <h1 className="font-display text-4xl md:text-7xl font-light tracking-tighter max-w-3xl">A buying experience that respects your time.</h1>
        <p className="text-gray-400 mt-6 max-w-2xl">Six steps. Most clients close in 5-10 days. No dealership lots, no haggling, no surprises.</p>
      </section>

      {/* Steps */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-10 grid md:grid-cols-2 gap-5">
        {STEPS.map((s) => (
          <div key={s.n} className="bg-[#0A0A0E] border border-[#1A1A24] p-7 rounded-2xl">
            <div className="flex items-center justify-between mb-5">
              <div className="label-mono">{s.n}</div>
              <s.icon className="w-5 h-5 text-silver" />
            </div>
            <div className="font-display text-2xl mb-3">{s.t}</div>
            <p className="text-gray-400 leading-relaxed text-sm">{s.d}</p>
          </div>
        ))}
      </section>

      {/* FAQ */}
      <section className="max-w-[900px] mx-auto px-5 lg:px-10 py-24" data-testid="faq-section">
        <p className="label-mono mb-3 text-center">FAQ</p>
        <h2 className="font-display text-3xl md:text-5xl font-light tracking-tighter text-center mb-12">Common questions.</h2>
        <div className="space-y-4">
          {FAQ.map((f, i) => (
            <details key={i} className="bg-[#0A0A0E] border border-[#1A1A24] rounded-xl p-5 group" data-testid={`faq-item-${i}`}>
              <summary className="cursor-pointer flex items-center justify-between font-display text-lg">
                {f.q}
                <span className="text-gray-500 group-open:rotate-45 transition">+</span>
              </summary>
              <p className="text-gray-400 mt-3 text-sm leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
        <div className="mt-12 text-center">
          <Link to="/booking" className="inline-flex px-8 py-4 rounded-full silver-gradient text-[#050507] font-bold tracking-wide">Book a Consultation</Link>
        </div>
      </section>
    </div>
  );
}
